import './spaPathFix';
import React, { useState, useRef, useEffect, memo, useCallback } from 'react';
import { createRoot } from 'react-dom/client';
import * as Sentry from '@sentry/react';
import './index.scss';
import 'overlayscrollbars/overlayscrollbars.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { UserProvider } from './config/UserContext';
import { SongsProvider } from './config/SongContext';
import { NavigationProvider } from './config/NavigationContext';
import { NotificationsProvider } from './config/NotificationContext';
import { PostFormProvider } from './config/PostFormContext';
import { ToastProvider } from './components/Toast';
import { ShareProvider } from './config/ShareContext';
import { AuthWrapper } from './config/AuthWrapper';
import { BrowserRouter, useLocation, useNavigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import SongPage from './components/SongPage/SongPage';
import MiniPlayer from './components/MiniPlayer';
import DefaultPage from './components/DefaultPage/DefaultPage';
import { getDoc, doc } from 'firebase/firestore';
import { db } from './config/firebase';
import ErrorBoundary from './components/ErrorBoundary';
import BadgeManager from './config/BadgeManager';
import StrikeModal from './components/StrikeModal';
import logger from './utils/logger';
import useAnalytics from './hooks/useAnalytics';
import { withFirestoreRetry, withTimeout } from './utils/firestoreRetry';

Sentry.init({
  dsn: process.env.REACT_APP_SENTRY_DSN,
  environment: process.env.NODE_ENV,
  enabled: Boolean(process.env.REACT_APP_SENTRY_DSN),
  integrations: [Sentry.browserTracingIntegration()],
  tracesSampleRate: 0.1,
  tracePropagationTargets: [
    'localhost',
    /^https:\/\/us-central1-ilovethissong-fd021\.cloudfunctions\.net/,
  ],
  // Plyr's XHR helper throws `new Error(0)` on network failure inside an event
  // listener (noembed, preview thumbs, etc.) — noise, not an app bug.
  beforeSend(event, hint) {
    const err = hint?.originalException;
    if (err) {
      const msg = String(err.message || '');
      // Plyr's XHR helper throws `new Error(0)` on network failure inside an
      // event listener (noembed, preview thumbs, etc.) — noise, not an app bug.
      if (msg === '0' && typeof err.stack === 'string' && err.stack.includes('plyr')) {
        return null;
      }
      // YouTube iframe API races during Plyr teardown when users switch or
      // close songs quickly — the iframe is gone but stop/pause still posts.
      if (msg === "Cannot read properties of null (reading 'src')") {
        const stack = typeof err.stack === 'string' ? err.stack : '';
        if (
          !stack ||
          stack.includes('www-widgetapi') ||
          stack.includes('widgetapi')
        ) {
          return null;
        }
      }
      // iOS Safari kills the IndexedDB connection when a tab is backgrounded.
      // Firebase uses IndexedDB for persistence; this is a browser lifecycle
      // event, not an app error.
      if (msg.includes('Connection to Indexed Database server lost')) {
        return null;
      }
      // Transient Firestore REST blips (tab backgrounded, flaky network, etc.).
      if (
        err?.name === 'FirebaseError' &&
        (err.code === 'unavailable' || msg.includes('Connection failed'))
      ) {
        return null;
      }
      // Chrome extension trying to message a closed/missing tab — not our code.
      if (msg.includes('runtime.sendMessage') && msg.includes('Tab not found')) {
        return null;
      }
      // Safari browser extension referencing a global that doesn't exist in our
      // bundle. EmptyRanges is not defined anywhere in our code or dependencies.
      if (err instanceof ReferenceError && msg.includes('EmptyRanges')) {
        return null;
      }
      // SW registration failures are expected in iOS private browsing, browsers
      // with SW disabled, and during CDN propagation windows after a deploy.
      // The app works fine without a service worker — not an actionable error.
      if (msg.includes('Failed to register a ServiceWorker')) {
        return null;
      }
      // Firebase Auth popup teardown race — sign-in often succeeds via
      // onAuthStateChanged before this delayed assertion surfaces.
      if (msg.includes('Pending promise was never set')) {
        return null;
      }
      // Firestore SDK internal queue races during overlapping listener writes.
      if (msg.includes('INTERNAL ASSERTION FAILED')) {
        return null;
      }
      // Instagram/Facebook Android in-app browser injects a navigation
      // performance logger that postMessages the native bridge on unload.
      // Failures there are Meta's IAB, not our app.
      if (msg.includes('Error invoking postMessage')) {
        const stack = typeof err.stack === 'string' ? err.stack : '';
        if (
          !stack ||
          stack.includes('iabjs://') ||
          stack.includes('navigation_performance_logger')
        ) {
          return null;
        }
      }
    }
    return event;
  },
});


const getSongById = async (songId) => {
  if (typeof songId !== 'string' || songId.trim() === '') {
    throw new Error('Invalid song ID');
  }
  const songDoc = await withTimeout(
    withFirestoreRetry(() => getDoc(doc(db, 'songs', songId))),
    8000
  );
  if (songDoc.exists()) {
    return { id: songDoc.id, ...songDoc.data() };
  }
  logger.warn('No such song!');
  return null;
};

// Optimized SongContainer with explicit lifecycle management
const SongContainer = memo(
  ({
    openSongPage,
    closeSongPage,
    currentSong,
    isSongLoading,
    targetCommentId,
    targetReplyId,
    isMinimized,
    minimizeSongPage,
    expandSongPage,
  }) => {
    const playerRef = useRef(null);
    const [isMobile, setIsMobile] = useState(() => window.innerWidth < 821);
    const [isPlaying, setIsPlaying] = useState(false);
    const [progress, setProgress] = useState(0);
    const mountedRef = useRef(false);

    const handlePlayStateChange = useCallback((playing) => {
      setIsPlaying(playing);
    }, []);

    const handlePlayPause = useCallback(() => {
      const player = playerRef.current;
      if (!player) return;
      if (player.isPlaying()) {
        player.pause();
        setIsPlaying(false);
      } else {
        player.play();
        setIsPlaying(true);
      }
    }, []);

    useEffect(() => {
      // Handle initial mount
      if (!mountedRef.current) {
        logger.debug('[SongContainer] Initial mount');
        mountedRef.current = true;
      }

      const handleResize = () => {
        const newIsMobile = window.innerWidth < 821;
        if (newIsMobile !== isMobile) {
          setIsMobile(newIsMobile);
        }
      };

      window.addEventListener('resize', handleResize);

      // Capture ref value for cleanup
      const playerInstance = playerRef.current;

      // Cleanup function
      return () => {
        window.removeEventListener('resize', handleResize);
        if (playerInstance?.destroy) {
          logger.debug('[SongContainer] Cleaning up player');
          playerInstance.destroy();
        }
      };
    }, [isMobile]);

    // Handle song changes
    useEffect(() => {
      if (currentSong) {
        logger.debug('[SongContainer] Song changed:', currentSong.id);
        setIsPlaying(false);
      }
    }, [currentSong]);

    const showDefaultPage = !currentSong && !isMobile;
    const shouldMinimize = isMinimized && isMobile && !!currentSong;

    // Sync isPlaying from the live Plyr state when the mini-player first appears
    useEffect(() => {
      if (shouldMinimize) {
        setIsPlaying(playerRef.current?.isPlaying?.() ?? false);
      }
    }, [shouldMinimize]);

    // Poll playback progress while mini-player is visible
    useEffect(() => {
      if (!shouldMinimize) {
        setProgress(0);
        return;
      }
      const id = setInterval(() => {
        setProgress(playerRef.current?.getProgress?.() ?? 0);
      }, 250);
      return () => clearInterval(id);
    }, [shouldMinimize]);

    const containerClassName = [
      'songPageContainer',
      showDefaultPage ? 'songPageContainer--scrollable' : '',
      shouldMinimize ? 'songPageContainer--minimized' : '',
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <>
        <div
          className={containerClassName}
          style={{
            display:
              currentSong || !isMobile
                ? showDefaultPage
                  ? 'flex'
                  : 'block'
                : 'none',
          }}
        >
          {currentSong ? (
            <SongPage
              key={currentSong.id}
              ref={playerRef}
              song={currentSong}
              onClose={closeSongPage}
              onMinimize={isMobile ? minimizeSongPage : undefined}
              openSongPage={openSongPage}
              isSongLoading={isSongLoading}
              targetCommentId={targetCommentId}
              targetReplyId={targetReplyId}
              onPlayStateChange={handlePlayStateChange}
            />
          ) : (
            !isMobile && <DefaultPage openSongPage={openSongPage} />
          )}
        </div>
        {shouldMinimize && (
          <MiniPlayer
            song={currentSong}
            onExpand={expandSongPage}
            onClose={closeSongPage}
            openSongPage={openSongPage}
            isPlaying={isPlaying}
            onPlayPause={handlePlayPause}
            progress={progress}
          />
        )}
      </>
    );
  }
);

SongContainer.displayName = 'SongContainer';

/**
 * Captures React Router's `navigate` into a ref so Root's stable handlers
 * (which live above BrowserRouter and are created once via handlersRef) can
 * drive router-aware navigation instead of raw `window.history.pushState`.
 * This keeps useLocation in sync with state changes, so the browser Back
 * button correctly reverses openSongPage/closeSongPage.
 */
const NavigatorBridge = ({ navigatorRef, children }) => {
  const navigate = useNavigate();
  navigatorRef.current = navigate;
  return children;
};

// Wrapper component to conditionally render app structure
const AppWrapper = ({
  openSongPage,
  closeSongPage,
  currentSong,
  isSongLoading,
  targetCommentId,
  targetReplyId,
  isMinimized,
  minimizeSongPage,
  expandSongPage,
}) => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const isStandaloneRoute =
    location.pathname === '/tos' || location.pathname === '/privacy';
  const isSharedSongRoute =
    location.pathname.startsWith('/song/') && searchParams.get('shared') === '1';

  // Standalone and shared-song routes render their own bare layout from App;
  // the right-panel SongContainer is part of the in-app shell, so we skip it.
  if (isStandaloneRoute || isSharedSongRoute) {
    return (
      <App
        openSongPage={openSongPage}
        closeSongPage={closeSongPage}
        currentSong={currentSong}
        isMinimized={isMinimized}
      />
    );
  }

  return (
    <div className="appWrapper">
      <App
        openSongPage={openSongPage}
        closeSongPage={closeSongPage}
        currentSong={currentSong}
        isMinimized={isMinimized}
      />
      <SongContainer
        openSongPage={openSongPage}
        closeSongPage={closeSongPage}
        currentSong={currentSong}
        isSongLoading={isSongLoading}
        targetCommentId={targetCommentId}
        targetReplyId={targetReplyId}
        isMinimized={isMinimized}
        minimizeSongPage={minimizeSongPage}
        expandSongPage={expandSongPage}
      />
    </div>
  );
};

// Main Root component with stable refs and handlers
const Root = () => {
  useAnalytics();
  const [currentSong, setCurrentSong] = useState(null);
  const [isSongLoading, setIsSongLoading] = useState(false);
  const [targetCommentId, setTargetCommentId] = useState(null);
  const [targetReplyId, setTargetReplyId] = useState(null);
  const [isMinimized, setIsMinimized] = useState(false);
  const scrollPositionRef = useRef(0);
  const isTransitioningRef = useRef(false);
  const navigatorRef = useRef(null);
  // Mirror the latest currentSong so the once-created openSongPage handler
  // can read it without being trapped by the null closure from first render.
  const currentSongRef = useRef(null);
  currentSongRef.current = currentSong;
  const handlersRef = useRef({
    openSongPage: null,
    closeSongPage: null,
  });
  // Stable refs to setters so the one-time-created handlers can call them
  const setTargetCommentIdRef = useRef(setTargetCommentId);
  const setTargetReplyIdRef = useRef(setTargetReplyId);
  const setIsMinimizedRef = useRef(setIsMinimized);
  const isMinimizedRef = useRef(false);
  setTargetCommentIdRef.current = setTargetCommentId;
  setTargetReplyIdRef.current = setTargetReplyId;
  setIsMinimizedRef.current = setIsMinimized;
  isMinimizedRef.current = isMinimized;

  // Route URL mutations through React Router when available so useLocation
  // stays in sync (fixes the browser Back button desync after openSongPage).
  // Falls back to raw history API on the very first render before the bridge
  // mounts.
  const pushUrl = (pathname, search) => {
    if (navigatorRef.current) {
      const currentUrl = window.location.pathname + window.location.search;
      const nextUrl = `${pathname}${search || ''}`;
      if (currentUrl === nextUrl) return; // avoid duplicate history entries
      navigatorRef.current({ pathname, search: search || '' });
    } else {
      window.history.pushState(null, '', `${pathname}${search || ''}`);
    }
  };

  // Create stable handlers that persist across renders
  if (!handlersRef.current.closeSongPage) {
    handlersRef.current.closeSongPage = () => {
      logger.debug('[Root] Closing song page');
      setCurrentSong(null);
      setIsSongLoading(false);
      setIsMinimizedRef.current(false);
      setTargetCommentIdRef.current(null);
      setTargetReplyIdRef.current(null);

      // Preserve the current pathname (e.g. /search, /user/xyz) and any
      // non-song query params (e.g. ?q=… on the search page) so closing
      // returns the user to the exact underlay they opened from, rather
      // than bouncing them to the home feed.
      const nextSearch = new URLSearchParams(window.location.search);
      nextSearch.delete('songId');
      nextSearch.delete('commentId');
      nextSearch.delete('replyId');
      const qs = nextSearch.toString();
      pushUrl(window.location.pathname, qs ? `?${qs}` : '');

      requestAnimationFrame(() => {
        window.scrollTo(0, scrollPositionRef.current);
      });
    };
  }

  if (!handlersRef.current.openSongPage) {
    handlersRef.current.openSongPage = async (songId, commentId, replyId) => {
      logger.debug('[Root] Request to open song:', songId);

      if (isTransitioningRef.current) {
        logger.debug('[Root] Transition already in progress');
        return;
      }

      if (songId === currentSongRef.current?.id) {
        logger.debug('[Root] Already on requested song');
        return;
      }

      try {
        isTransitioningRef.current = true;
        scrollPositionRef.current = window.scrollY;
        setIsSongLoading(true);
        if (!isMinimizedRef.current) {
          setIsMinimizedRef.current(false);
        }

        let nextSong;
        try {
          nextSong = await getSongById(songId);
        } catch (fetchError) {
          logger.warn('[Root] Song fetch failed, cannot navigate:', fetchError);
          alert("Couldn't open that song — check your connection and try again.");
          return;
        }
        if (!nextSong) {
          logger.warn('[Root] Song not found, cannot navigate');
          alert('The song you are trying to view has been deleted.');
          return;
        }

        logger.debug('[Root] Setting current song:', nextSong.id);
        setCurrentSong(nextSong);
        setTargetCommentIdRef.current(commentId || null);
        setTargetReplyIdRef.current(replyId || null);

        // Preserve the current pathname AND any existing query params
        // (e.g. ?q=… on /search) so opening a song keeps the underlay's
        // state intact — closeSongPage will strip songId/commentId/replyId
        // to return the user to that exact state.
        const nextSearch = new URLSearchParams(window.location.search);
        nextSearch.set('songId', songId);
        if (commentId) nextSearch.set('commentId', commentId);
        else nextSearch.delete('commentId');
        if (replyId) nextSearch.set('replyId', replyId);
        else nextSearch.delete('replyId');
        pushUrl(window.location.pathname, `?${nextSearch.toString()}`);
      } catch (error) {
        logger.error('[Root] Error during song transition:', error);
      } finally {
        isTransitioningRef.current = false;
        setIsSongLoading(false);
      }
    };
  }

  const minimizeSongPage = useCallback(() => setIsMinimized(true), []);
  const expandSongPage = useCallback(() => setIsMinimized(false), []);

  // Strike solidarity: set REACT_APP_STRIKE_MODE=false and redeploy when strike is over
  const showStrikeModal = process.env.REACT_APP_STRIKE_MODE === 'true';

  // Only use StrictMode in development
  const appContent = (
    <ErrorBoundary>
      {showStrikeModal && <StrikeModal />}
      <UserProvider>
        <SongsProvider>
          <AuthWrapper>
            <NotificationsProvider>
              <PostFormProvider>
                <ToastProvider>
                  <ShareProvider>
                    <BrowserRouter>
                      <NavigatorBridge navigatorRef={navigatorRef}>
                        <NavigationProvider>
                          <BadgeManager />
                          <AppWrapper
                            openSongPage={handlersRef.current.openSongPage}
                            closeSongPage={handlersRef.current.closeSongPage}
                            currentSong={currentSong}
                            isSongLoading={isSongLoading}
                            targetCommentId={targetCommentId}
                            targetReplyId={targetReplyId}
                            isMinimized={isMinimized}
                            minimizeSongPage={minimizeSongPage}
                            expandSongPage={expandSongPage}
                          />
                        </NavigationProvider>
                      </NavigatorBridge>
                    </BrowserRouter>
                  </ShareProvider>
                </ToastProvider>
              </PostFormProvider>
            </NotificationsProvider>
          </AuthWrapper>
        </SongsProvider>
      </UserProvider>
    </ErrorBoundary>
  );

  useEffect(() => {
    document.documentElement.style.setProperty(
      '--mini-player-height',
      isMinimized ? '56px' : '0px'
    );
  }, [isMinimized]);

  // Log song changes
  useEffect(() => {
    if (currentSong) {
      logger.debug('[Root] Current song updated:', currentSong.id);
    }
  }, [currentSong]);

  return process.env.NODE_ENV === 'development' ? (
    <React.StrictMode>
      <HelmetProvider>{appContent}</HelmetProvider>
    </React.StrictMode>
  ) : (
    <HelmetProvider>{appContent}</HelmetProvider>
  );
};

const container = document.getElementById('root');
const root = createRoot(container);
root.render(<Root />);

reportWebVitals();

// Register Service Worker in production to ensure fresh app shell handling
if (
  typeof window !== 'undefined' &&
  'serviceWorker' in navigator &&
  (process.env.NODE_ENV === 'production' ||
    process.env.REACT_APP_SW_LOCAL === '1')
) {
  if (
    process.env.NODE_ENV === 'production' ||
    process.env.REACT_APP_SW_LOCAL === '1'
  ) {
    window.addEventListener('load', () => {
      navigator.serviceWorker
        .register('/sw.js', { scope: '/', updateViaCache: 'none' })
        .then((registration) => {
          logger.info('SW registered', { scope: registration.scope });
        })
        .catch((error) => {
          logger.error('SW registration failed', error);
        });
    });
  }
}
